import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { agentsApi, queuesApi, dialingProfilesApi } from '../../../api/client';
import Modal, { Button } from '../../ui/Modal';
import { Label, Input, ErrorBanner } from '../../../auth/AuthShell';

const EXT_RE = /^\d{4,6}$/;

export default function AgentEditModal({ agent, onClose, onSaved }) {
  const isNew = !agent;
  const [form, setForm] = useState(() => ({
    extension:      agent?.extension      || '',
    name:           agent?.name           || '',
    email:          agent?.email          || '',
    queues:         agent?.queues || [],   // array of queue_id strings
    winid:          agent?.winid          || '',
    skill_level:    agent?.skill_level    || 1,
    callerid_name:  agent?.callerid_name  || '',
    callerid_number:agent?.callerid_number|| '',
    language:       agent?.language       || 'en',
    recording_enabled: agent ? agent.recording_enabled : true,
    screen_recording_enabled: agent
      ? (agent.screen_recording_enabled !== undefined
         ? agent.screen_recording_enabled
         : true)
      : true,
    wrap_time_sec:  agent?.wrap_time_sec  || 15,
    max_calls:      agent?.max_calls      || 1,
    enabled:        agent ? agent.enabled !== false : true,
    rotate_sip_password: false,
    dialing_profile_id: agent?.dialing_profile_id || '',
  }));
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) {
        if (!EXT_RE.test(form.extension)) throw new Error('Extension must be 4-6 digits.');
        if (!form.email.toLowerCase().endsWith('@continuumgbl.com')) {
          throw new Error('Email must end with @continuumgbl.com.');
        }
        if (!form.name.trim()) throw new Error('Name is required.');
      }

      const payload = {
        ...(isNew ? { extension: form.extension, email: form.email.toLowerCase() } : {}),
        name: form.name.trim(),
        queues: Array.from(new Set((form.queues || []).map((q) => String(q).trim()).filter(Boolean))),
        winid: form.winid.trim() || null,
        skill_level: Number(form.skill_level),
        callerid_name: form.callerid_name.trim() || null,
        callerid_number: form.callerid_number.trim() || null,
        language: form.language || 'en',
        recording_enabled: !!form.recording_enabled,
        screen_recording_enabled: !!form.screen_recording_enabled,
        wrap_time_sec: Number(form.wrap_time_sec),
        max_calls: Number(form.max_calls),
        enabled: !!form.enabled,
        dialing_profile_id: form.dialing_profile_id || '',
      };
      if (!isNew && form.rotate_sip_password) payload.rotate_sip_password = true;

      return isNew
        ? agentsApi.create(payload)
        : agentsApi.update(agent.id, payload);
    },
    onSuccess: onSaved,
    onError:   (err) => setError(err?.detail || err?.message || 'Save failed.'),
  });

  const onSubmit = (e) => { e.preventDefault(); setError(null); save.mutate(); };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={isNew ? 'New agent' : `Edit agent ${agent.extension}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={onSubmit} loading={save.isPending}>
            {isNew ? 'Create' : 'Save changes'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <ErrorBanner>{error}</ErrorBanner>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="extension">Extension</Label>
            <Input
              id="extension"
              value={form.extension}
              onChange={(e) => set('extension', e.target.value.replace(/\D/g, '').slice(0, 6))}
              disabled={!isNew}
              required
              pattern="\d{4,6}"
              className="font-mono"
              placeholder="60001"
            />
            <p className="mt-1 text-xs text-surface-muted">4–6 digits. Can't change after create.</p>
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              disabled={!isNew}
              required
              placeholder="agent@continuumgbl.com"
            />
            <p className="mt-1 text-xs text-surface-muted">Must end with @continuumgbl.com.</p>
          </div>
        </div>

        <div>
          <Label htmlFor="name">Display name</Label>
          <Input id="name" value={form.name} onChange={(e) => set('name', e.target.value)} required />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="winid">WIN ID</Label>
            <Input id="winid" value={form.winid} onChange={(e) => set('winid', e.target.value)} placeholder="optional" />
          </div>
          <div>
            <Label htmlFor="skill_level">Skill level (1–5)</Label>
            <Input
              id="skill_level"
              type="number"
              min={1}
              max={5}
              value={form.skill_level}
              onChange={(e) => set('skill_level', e.target.value)}
            />
          </div>
        </div>

        <QueuesPicker
          value={form.queues}
          onChange={(v) => set('queues', v)}
        />

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="callerid_name">Caller-ID name</Label>
            <Input id="callerid_name" value={form.callerid_name} onChange={(e) => set('callerid_name', e.target.value)} placeholder="optional" />
          </div>
          <div>
            <Label htmlFor="callerid_number">Caller-ID number</Label>
            <Input id="callerid_number" value={form.callerid_number} onChange={(e) => set('callerid_number', e.target.value)} placeholder="optional E.164" />
          </div>
        </div>

        <DialingProfilePicker
          value={form.dialing_profile_id}
          onChange={(v) => set('dialing_profile_id', v)}
        />

        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label htmlFor="language">Language</Label>
            <select
              id="language"
              value={form.language}
              onChange={(e) => set('language', e.target.value)}
              className="w-full px-3 py-2 rounded-thin bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border text-sm"
            >
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
            </select>
          </div>
          <div>
            <Label htmlFor="wrap_time_sec">Wrap-up (sec)</Label>
            <Input
              id="wrap_time_sec"
              type="number"
              min={0}
              max={600}
              value={form.wrap_time_sec}
              onChange={(e) => set('wrap_time_sec', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="max_calls">Max calls</Label>
            <Input
              id="max_calls"
              type="number"
              min={1}
              max={20}
              value={form.max_calls}
              onChange={(e) => set('max_calls', e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center gap-6 flex-wrap">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.recording_enabled}
                   onChange={(e) => set('recording_enabled', e.target.checked)} />
            Voice recording
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.screen_recording_enabled}
                   onChange={(e) => set('screen_recording_enabled', e.target.checked)} />
            Screen recording
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.enabled}
                   onChange={(e) => set('enabled', e.target.checked)} />
            Account enabled
          </label>
        </div>

        {!isNew && (
          <div className="border-t border-surface-border dark:border-surface-dark-border pt-3">
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={form.rotate_sip_password}
                     onChange={(e) => set('rotate_sip_password', e.target.checked)} className="mt-0.5" />
              <span>
                <strong>Rotate SIP password</strong>
                <span className="block text-xs text-surface-muted">
                  Invalidates current bundle. Existing softphone sessions will fail on next REGISTER and need to re-fetch credentials (happens automatically on next portal load).
                </span>
              </span>
            </label>
          </div>
        )}

        {isNew && (
          <div className="text-xs text-surface-muted bg-surface-subtle dark:bg-surface-dark-subtle p-3 rounded-thin">
            On create: system generates a temp portal password (emailed to the agent), generates a random 32-char SIP password (stored encrypted, never shown to the agent), and writes the digest hash to the Kamailio subscriber table. Agent receives portal login link; SIP credentials are fetched server-side after their portal MFA login.
          </div>
        )}
      </form>
    </Modal>
  );
}


/**
 * Queue picker — multi-select chip list.
 *
 * Loads queues from the admin endpoint. Shows assigned + available in one
 * flat list; clicking a chip toggles it. Includes a search box so a
 * 200-queue catalog stays usable.
 */
function QueuesPicker({ value, onChange }) {
  const [filter, setFilter] = useState('');
  const { data: queues = [], isLoading, error } = useQuery({
    queryKey: ['admin', 'queues'],
    queryFn: () => queuesApi.list(),
    staleTime: 60_000,
  });

  const selected = useMemo(() => new Set(value || []), [value]);

  // Surface queue_ids stored on the agent but not in the live queue list
  // (e.g. legacy Issabel-GUI 1xxx queues). Keep them so editing a legacy
  // agent doesn't silently drop their assignments.
  const known = useMemo(() => new Set(queues.map((q) => q.queue_id)), [queues]);
  const extras = useMemo(
    () => (value || []).filter((q) => !known.has(q)).map((q) => ({ queue_id: q, name: '(legacy / external)', _orphan: true })),
    [value, known]
  );

  const allChips = useMemo(() => [...queues, ...extras], [queues, extras]);

  const filtered = useMemo(() => {
    if (!filter.trim()) return allChips;
    const f = filter.toLowerCase();
    return allChips.filter(
      (q) => q.queue_id.includes(f) || (q.name || '').toLowerCase().includes(f)
    );
  }, [allChips, filter]);

  const toggle = (qid) => {
    const next = new Set(value || []);
    if (next.has(qid)) next.delete(qid); else next.add(qid);
    onChange(Array.from(next));
  };
  const clear = () => onChange([]);
  const selectAll = () => onChange(allChips.map((q) => q.queue_id));

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label>Queues</Label>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-surface-muted">
            {selected.size} / {allChips.length} selected
          </span>
          {selected.size > 0 && (
            <button type="button" onClick={clear} className="text-red-600 hover:underline">Clear</button>
          )}
          {allChips.length > 0 && selected.size < allChips.length && (
            <button type="button" onClick={selectAll} className="text-continuum-700 dark:text-continuum-300 hover:underline">All</button>
          )}
        </div>
      </div>
      {allChips.length > 8 && (
        <input
          type="search"
          placeholder="Filter queues…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full px-2 py-1 mb-2 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin text-sm"
        />
      )}
      <div className="flex flex-wrap gap-2 p-2 border border-surface-border dark:border-surface-dark-border rounded-thin min-h-[42px]">
        {isLoading && <span className="text-xs text-surface-muted">Loading…</span>}
        {error && <span className="text-xs text-red-600">Failed to load queues</span>}
        {!isLoading && filtered.length === 0 && (
          <span className="text-xs text-surface-muted">
            {filter ? 'No queues match this filter.' : 'No queues configured. Create one in Admin › Queues.'}
          </span>
        )}
        {filtered.map((q) => {
          const on = selected.has(q.queue_id);
          return (
            <button
              key={q.queue_id}
              type="button"
              onClick={() => toggle(q.queue_id)}
              title={q._orphan ? 'This queue is not portal-managed; kept for legacy compatibility.' : undefined}
              className={
                'inline-flex items-center gap-1 px-2 py-1 rounded-thin text-xs border transition-colors ' +
                (on
                  ? 'bg-continuum-500 hover:bg-continuum-600 text-white border-continuum-500'
                  : 'border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle ' +
                    (q._orphan ? 'opacity-70' : ''))
              }
            >
              <span className="font-mono">{q.queue_id}</span>
              <span className={on ? 'opacity-90' : 'opacity-60'}>· {q.name}</span>
              {on && <span aria-hidden="true">✓</span>}
            </button>
          );
        })}
      </div>
      <p className="mt-1 text-xs text-surface-muted">
        Click to toggle. Saving here updates the database; queue config is pushed to LOU Issabel on the next ↻ Re-sync all from Admin › Queues.
      </p>
    </div>
  );
}

// -----------------------------------------------------------
// Outbound dialing profile picker (added 2026-06-18)
// -----------------------------------------------------------

function DialingProfilePicker({ value, onChange }) {
  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ['dialing-profiles'],
    queryFn:  dialingProfilesApi.list,
  });
  const selected = profiles.find(p => p.id === value);
  const isDefaultBlock = !value || selected?.is_default_block;
  return (
    <div>
      <Label htmlFor="dialing_profile_id">Outbound dialing profile</Label>
      <select
        id="dialing_profile_id"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={isLoading}
        className="w-full px-3 py-2 rounded-thin bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border text-sm"
      >
        <option value="">— Default Block (all PSTN denied) —</option>
        {profiles
          .filter(p => !p.is_default_block)
          .map(p => (
            <option key={p.id} value={p.id}>
              {p.name}
              {' · '}
              {[
                p.allow_domestic_us && 'US',
                p.allow_canada && 'CA',
                p.allow_mexico && 'MX',
                p.allow_intl_other && 'Intl',
              ].filter(Boolean).join('/') || 'no regions'}
            </option>
          ))}
      </select>
      <p className={`text-xs mt-1 ${isDefaultBlock ? 'text-amber-600 dark:text-amber-400' : 'text-surface-muted dark:text-surface-dark-muted'}`}>
        {isDefaultBlock
          ? '⚠️ No profile selected — agent cannot dial any external PSTN number.'
          : `Manage profiles in Admin > Outbound Dialing Profiles. Telnyx zone permissions still apply.`}
      </p>
    </div>
  );
}
